import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL, getAuthConfig } from "./shared";

/*
 * Laboratoire — Agenda (cahier des charges labo, §6, §8-9, §23-25), DEDIE.
 *
 * Port de AgendaModule (HopitalDashboard.js), repointe sur les routes labo
 * dediees : /api/lab/agenda/* (deja forke, PR3b), /api/lab/act-types
 * (fork PR6c), /api/lab/patients (recherche patient), /api/lab/schedule/*
 * (nouveau fork PR6c, backend/routes/lab/schedule_config.py — comble la
 * limitation ou seul le proprietaire du labo pouvait configurer ses
 * horaires via l'ancienne route /api/sih/schedule/*). Logique identique a
 * l'original, aucune branche "isLab" (toujours vrai ici).
 */

const AGENDA_STATUS_LABELS = { confirme: "Confirme", annule: "Annule", termine: "Termine", no_show: "Absent" };
const AGENDA_STATUS_COLORS = {
  confirme: ["#E6F1FB", "#185FA5"],
  annule: ["#F1F5F9", "#94A3B8"],
  termine: ["#DCFCE7", "#166534"],
  no_show: ["#FEF2E8", "#B45309"],
};

// Gestion des creneaux avancee (cahier des charges labo, section 25) :
// index de jour = date.getDay() ramene a la convention backend (lundi = 0),
// voir routes/lab/schedule_config.py.
const SCHEDULE_WEEKDAY_LABELS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function AgendaModule({ can }) {
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actTypes, setActTypes] = useState([]);

  // Vues semaine/mois (cahier des charges labo, section 6.1 : les 4 vues de
  // l'agenda sont journee/semaine/mois/liste). "liste" reste le comportement
  // par defaut inchange ; semaine/mois chargent une periode via le meme
  // endpoint (date_from/date_to) puis groupent cote client par jour.
  const [viewMode, setViewMode] = useState("liste");
  const [rangeAppointments, setRangeAppointments] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);

  // Assistant de creation / deplacement (meme modale pour les deux usages).
  const [wizard, setWizard] = useState(null); // { mode: "create"|"move", appointment? }
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [actTypeId, setActTypeId] = useState("");
  const [wizDate, setWizDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [wizError, setWizError] = useState("");

  // Liste d'attente (section 24) : modale separee du wizard RDV (recherche
  // patient propre) pour ne pas melanger les deux flux.
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [wlSearch, setWlSearch] = useState("");
  const [wlResults, setWlResults] = useState([]);
  const [wlPatient, setWlPatient] = useState(null);
  const [wlActTypeId, setWlActTypeId] = useState("");
  const [wlDate, setWlDate] = useState("");
  const [wlNotes, setWlNotes] = useState("");
  const [wlSaving, setWlSaving] = useState(false);
  const [wlError, setWlError] = useState("");

  // Gestion des creneaux avancee (cahier des charges labo, section 25) :
  // horaires hebdomadaires (avec pauses), capacite simultanee, fermetures
  // exceptionnelles. Chargee a la demande (pas au montage du module) : c'est
  // un ecran de configuration, pas un affichage du quotidien.
  const [showSchedule, setShowSchedule] = useState(false);
  const [weekly, setWeekly] = useState(null);
  const [capacity, setCapacity] = useState(1);
  const [confirmRequired, setConfirmRequired] = useState(false);
  const [confirmDeadlineHours, setConfirmDeadlineHours] = useState(24);
  const [closures, setClosures] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState("");
  const [newClosureDate, setNewClosureDate] = useState("");
  const [newClosureFullDay, setNewClosureFullDay] = useState(true);
  const [newClosureStart, setNewClosureStart] = useState("");
  const [newClosureEnd, setNewClosureEnd] = useState("");
  const [newClosureReason, setNewClosureReason] = useState("");

  const load = () => {
    setLoading(true); setError("");
    axios.get(`${API_URL}/api/lab/agenda/appointments`, { ...getAuthConfig(), params: { date } })
      .then((r) => setAppointments(r.data.appointments || []))
      .catch(() => setError("Impossible de charger l'agenda"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => {
    axios.get(`${API_URL}/api/lab/act-types`, getAuthConfig()).then((r) => setActTypes(r.data.act_types || [])).catch(() => {});
  }, []);

  // Bornes de la semaine (lundi-dimanche) contenant `date`.
  const weekDays = () => {
    const d = new Date(`${date}T00:00:00`);
    const dow = d.getDay(); // 0=dimanche..6=samedi
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(d); monday.setDate(d.getDate() + mondayOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday); day.setDate(monday.getDate() + i);
      return day.toISOString().slice(0, 10);
    });
  };
  // Bornes du mois (1er au dernier jour) contenant `date`.
  const monthBounds = () => {
    const d = new Date(`${date}T00:00:00`);
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { first: first.toISOString().slice(0, 10), last: last.toISOString().slice(0, 10), year: d.getFullYear(), month: d.getMonth() };
  };
  useEffect(() => {
    if (viewMode === "liste") return;
    setRangeLoading(true);
    const [from, to] = viewMode === "semaine"
      ? [weekDays()[0], weekDays()[6]]
      : [monthBounds().first, monthBounds().last];
    axios.get(`${API_URL}/api/lab/agenda/appointments`, { ...getAuthConfig(), params: { date_from: from, date_to: to } })
      .then((r) => setRangeAppointments(r.data.appointments || []))
      .catch(() => setRangeAppointments([]))
      .finally(() => setRangeLoading(false));
    // eslint-disable-next-line
  }, [viewMode, date]);
  const appointmentsByDay = () => {
    const map = {};
    for (const a of rangeAppointments) { (map[a.date] = map[a.date] || []).push(a); }
    return map;
  };

  const loadWaitlist = () => {
    axios.get(`${API_URL}/api/lab/agenda/waitlist`, { ...getAuthConfig(), params: { status: "all" } })
      .then((r) => {
        const items = r.data.waitlist || [];
        setWaitlist(items);
        setWaitlistCount(items.filter((w) => w.status === "attente" || w.status === "notifie").length);
      })
      .catch(() => {});
  };
  useEffect(() => { loadWaitlist(); }, []);

  const resetWaitlistForm = () => {
    setWlSearch(""); setWlResults([]); setWlPatient(null);
    setWlActTypeId(""); setWlDate(""); setWlNotes(""); setWlError("");
  };
  const doWlSearch = async (q) => {
    setWlSearch(q);
    if (q.trim().length < 2) { setWlResults([]); return; }
    try {
      const r = await axios.get(`${API_URL}/api/lab/patients`, { ...getAuthConfig(), params: { search: q, limit: 8 } });
      setWlResults(r.data.patients || []);
    } catch (e) { setWlResults([]); }
  };
  const addToWaitlist = async () => {
    if (!wlPatient) return;
    setWlSaving(true); setWlError("");
    try {
      await axios.post(`${API_URL}/api/lab/agenda/waitlist`, {
        patient_id: wlPatient.patient_id, act_type_id: wlActTypeId || null,
        desired_date: wlDate || null, notes: wlNotes,
      }, getAuthConfig());
      toast.success("Patient ajoute a la liste d'attente");
      resetWaitlistForm(); loadWaitlist();
    } catch (e) {
      setWlError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setWlSaving(false); }
  };
  const setWaitlistStatus = async (entry, status) => {
    try {
      await axios.put(`${API_URL}/api/lab/agenda/waitlist/${entry.waitlist_id}`, { status }, getAuthConfig());
      loadWaitlist();
    } catch (e) { toast.error("Erreur"); }
  };

  // Gestion des creneaux avancee (cahier des charges labo, section 25).
  const loadSchedule = () => {
    setScheduleLoading(true); setScheduleError("");
    Promise.all([
      axios.get(`${API_URL}/api/lab/schedule/config`, getAuthConfig()),
      axios.get(`${API_URL}/api/lab/schedule/closures`, getAuthConfig()),
    ]).then(([rc, rf]) => {
      setWeekly(rc.data.weekly); setCapacity(rc.data.capacity);
      setConfirmRequired(!!rc.data.confirmation?.required);
      setConfirmDeadlineHours(rc.data.confirmation?.deadline_hours || 24);
      setClosures(rf.data.closures || []);
    }).catch(() => setScheduleError("Impossible de charger la configuration"))
      .finally(() => setScheduleLoading(false));
  };
  const openSchedule = () => { setShowSchedule(true); loadSchedule(); };
  const toggleDayOpen = (day, isOpen) => {
    setWeekly((w) => ({ ...w, [day]: isOpen ? [{ start: "08:00", end: "17:00" }] : [] }));
  };
  const setRangeField = (day, idx, field, value) => {
    setWeekly((w) => {
      const ranges = [...(w[day] || [])];
      ranges[idx] = { ...ranges[idx], [field]: value };
      return { ...w, [day]: ranges };
    });
  };
  const addRange = (day) => {
    setWeekly((w) => ({ ...w, [day]: [...(w[day] || []), { start: "13:00", end: "17:00" }] }));
  };
  const removeRange = (day, idx) => {
    setWeekly((w) => {
      const ranges = [...(w[day] || [])];
      ranges.splice(idx, 1);
      return { ...w, [day]: ranges };
    });
  };
  const saveSchedule = async () => {
    setScheduleSaving(true); setScheduleError("");
    try {
      await axios.put(`${API_URL}/api/lab/schedule/config`, {
        weekly, capacity,
        confirmation: { required: confirmRequired, deadline_hours: confirmDeadlineHours },
      }, getAuthConfig());
      toast.success("Horaires mis a jour");
    } catch (e) {
      setScheduleError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'enregistrement");
    } finally { setScheduleSaving(false); }
  };
  const addClosure = async () => {
    if (!newClosureDate) return;
    setScheduleError("");
    try {
      await axios.post(`${API_URL}/api/lab/schedule/closures`, {
        date: newClosureDate,
        start: newClosureFullDay ? null : (newClosureStart || null),
        end: newClosureFullDay ? null : (newClosureEnd || null),
        reason: newClosureReason,
      }, getAuthConfig());
      setNewClosureDate(""); setNewClosureFullDay(true); setNewClosureStart(""); setNewClosureEnd(""); setNewClosureReason("");
      loadSchedule();
    } catch (e) {
      setScheduleError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur lors de l'ajout");
    }
  };
  const removeClosure = async (c) => {
    try {
      await axios.delete(`${API_URL}/api/lab/schedule/closures/${c.closure_id}`, getAuthConfig());
      loadSchedule();
    } catch (e) { toast.error("Erreur"); }
  };

  const resetWizard = () => {
    setWizard(null); setSearch(""); setResults([]); setPatient(null);
    setActTypeId(""); setWizDate(todayStr()); setSlots([]); setNotes(""); setWizError("");
  };

  const openCreate = () => { resetWizard(); setWizDate(date); setWizard({ mode: "create" }); };
  const openMove = (apt) => {
    resetWizard();
    setWizDate(apt.date);
    setActTypeId(apt.act_type_id || "");
    setWizard({ mode: "move", appointment: apt });
  };

  const doSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults([]); return; }
    try {
      const r = await axios.get(`${API_URL}/api/lab/patients`, { ...getAuthConfig(), params: { search: q, limit: 8 } });
      setResults(r.data.patients || []);
    } catch (e) { setResults([]); }
  };

  const currentDuration = () => {
    const a = actTypes.find((a) => a.act_type_id === actTypeId);
    return a ? a.duration_minutes : 15;
  };

  // Recharge les creneaux disponibles a chaque changement de date/acte, aussi
  // bien en creation qu'en deplacement.
  useEffect(() => {
    if (!wizard || !wizDate) { setSlots([]); return; }
    setLoadingSlots(true);
    axios.get(`${API_URL}/api/lab/agenda/slots`, {
      ...getAuthConfig(),
      params: { date: wizDate, act_type_id: actTypeId || undefined, duration_minutes: actTypeId ? undefined : 15 },
    })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
    // eslint-disable-next-line
  }, [wizard, wizDate, actTypeId]);

  const confirmCreate = async (time) => {
    if (!patient) return;
    setSaving(true); setWizError("");
    try {
      await axios.post(`${API_URL}/api/lab/agenda/appointments`, {
        patient_id: patient.patient_id, act_type_id: actTypeId || null, date: wizDate, time, notes,
      }, getAuthConfig());
      toast.success("Rendez-vous cree");
      resetWizard(); setDate(wizDate); load();
    } catch (e) {
      setWizError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const confirmMove = async (time) => {
    if (!wizard?.appointment) return;
    setSaving(true); setWizError("");
    try {
      await axios.put(`${API_URL}/api/lab/agenda/appointments/${wizard.appointment.appointment_id}`, {
        date: wizDate, time,
      }, getAuthConfig());
      toast.success("Rendez-vous deplace");
      resetWizard(); setDate(wizDate); load();
    } catch (e) {
      setWizError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const setStatus = async (apt, status) => {
    try {
      await axios.put(`${API_URL}/api/lab/agenda/appointments/${apt.appointment_id}`, { status }, getAuthConfig());
      load();
    } catch (e) {
      // Correctif L-STATUS-TRANSITION : voir routes/lab/agenda.py — afficher
      // le message explicite du backend plutot qu'un "Erreur" generique.
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Agenda</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...iS, width: "auto" }} />
          <div style={{ display: "flex", border: "1px solid #CBD5E1", borderRadius: 8, overflow: "hidden" }}>
            {[["liste", "Liste"], ["semaine", "Semaine"], ["mois", "Mois"]].map(([id, label]) => (
              <button key={id} onClick={() => setViewMode(id)}
                style={{
                  padding: "8px 14px", fontSize: 13, border: "none", cursor: "pointer",
                  background: viewMode === id ? "#185FA5" : "#fff",
                  color: viewMode === id ? "#fff" : "#334155", fontWeight: viewMode === id ? 600 : 400,
                }}>{label}</button>
            ))}
          </div>
          {can("agenda:read") && (
            <button onClick={() => { resetWaitlistForm(); setShowWaitlist(true); }} style={{ padding: "9px 16px", background: "#fff", color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer", position: "relative" }}>
              Liste d'attente
              {waitlistCount > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: "#F59E0B", color: "#fff", borderRadius: 10, padding: "1px 7px" }}>{waitlistCount}</span>}
            </button>
          )}
          {can("schedule:manage") && (
            <button onClick={openSchedule} style={{ padding: "9px 16px", background: "#fff", color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              Horaires
            </button>
          )}
          {can("agenda:manage") && <button onClick={openCreate} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>+ Nouveau rendez-vous</button>}
        </div>
      </div>

      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {viewMode === "liste" && (
      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Heure</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Patient</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Acte</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Chargement...</td></tr>}
            {!loading && appointments.length === 0 && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun rendez-vous ce jour.</td></tr>}
            {!loading && appointments.map((a) => { const sc = AGENDA_STATUS_COLORS[a.status] || ["#F1F5F9", "#64748B"]; return (
              <tr key={a.appointment_id} style={{ borderTop: "1px solid #F1F5F9", opacity: (a.status === "annule" || a.status === "no_show") ? 0.55 : 1 }}>
                <td style={{ padding: "12px 14px", fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{a.time}</td>
                <td style={{ padding: "12px 14px", fontSize: 14 }}><div style={{ fontWeight: 500 }}>{a.patient_name || "—"}</div>{a.patient_number && <div style={{ fontSize: 11, color: "#94A3B8" }}>{a.patient_number}</div>}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{a.act_type_name || "—"}{a.duration_minutes ? ` (${a.duration_minutes} min)` : ""}</td>
                <td style={{ padding: "12px 14px" }}>
                  <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{AGENDA_STATUS_LABELS[a.status] || a.status}</span>
                  {a.confirmation_status === "en_attente" && <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#F1F5F9", color: "#64748B" }}>Confirmation attendue</span>}
                  {a.confirmation_status === "non_confirme" && <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 8px", borderRadius: 10, background: "#FEF2E8", color: "#B45309", fontWeight: 600 }}>Non confirme</span>}
                </td>
                <td style={{ padding: "12px 14px" }}><span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => openMove(a)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Deplacer</button>}
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => setStatus(a, "termine")} style={{ fontSize: 13, color: "#166534", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Terminer</button>}
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => setStatus(a, "no_show")} style={{ fontSize: 13, color: "#B45309", background: "none", border: "none", cursor: "pointer" }}>Absent</button>}
                  {can("agenda:manage") && a.status === "confirme" && <button onClick={() => setStatus(a, "annule")} style={{ fontSize: 13, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Annuler</button>}
                </span></td>
              </tr>); })}
          </tbody>
        </table>
      </div>
      )}

      {viewMode === "semaine" && (
        <div style={{ marginTop: 16 }}>
          {rangeLoading && <div style={{ padding: 16, fontSize: 13, color: "#94A3B8" }}>Chargement...</div>}
          {!rangeLoading && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(140px, 1fr))", gap: 10, overflowX: "auto" }}>
              {weekDays().map((d) => {
                const dayAppts = (appointmentsByDay()[d] || []).slice().sort((a, b) => a.time.localeCompare(b.time));
                const isToday = d === todayStr();
                const label = new Date(`${d}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
                return (
                  <div key={d} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, minHeight: 220, overflow: "hidden" }}>
                    <div
                      onClick={() => { setDate(d); setViewMode("liste"); }}
                      style={{ padding: "8px 10px", fontSize: 12, fontWeight: 700, textTransform: "capitalize", cursor: "pointer", background: isToday ? "#E6F1FB" : "#F8FAFC", color: isToday ? "#185FA5" : "#334155" }}
                    >{label}</div>
                    <div style={{ padding: 8 }}>
                      {dayAppts.length === 0 && <div style={{ fontSize: 12, color: "#CBD5E1", padding: "6px 4px" }}>—</div>}
                      {dayAppts.map((a) => {
                        const sc = AGENDA_STATUS_COLORS[a.status] || ["#F1F5F9", "#64748B"];
                        return (
                          <div key={a.appointment_id} onClick={() => { setDate(d); setViewMode("liste"); }}
                            style={{ fontSize: 12, padding: "5px 7px", marginBottom: 5, borderRadius: 6, background: sc[0], color: sc[1], cursor: "pointer", opacity: (a.status === "annule" || a.status === "no_show") ? 0.55 : 1 }}>
                            <div style={{ fontWeight: 700 }}>{a.time}</div>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.patient_name || "—"}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {viewMode === "mois" && (
        <div style={{ marginTop: 16 }}>
          {rangeLoading && <div style={{ padding: 16, fontSize: 13, color: "#94A3B8" }}>Chargement...</div>}
          {!rangeLoading && (() => {
            const { year, month } = monthBounds();
            const firstOfMonth = new Date(year, month, 1);
            const startOffset = firstOfMonth.getDay() === 0 ? 6 : firstOfMonth.getDay() - 1; // lundi = 0
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const cells = [];
            for (let i = 0; i < startOffset; i++) cells.push(null);
            for (let day = 1; day <= daysInMonth; day++) cells.push(day);
            while (cells.length % 7 !== 0) cells.push(null);
            const byDay = appointmentsByDay();
            const monthLabel = firstOfMonth.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
            return (
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0F172A", marginBottom: 10, textTransform: "capitalize" }}>{monthLabel}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                  {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
                    <div key={d} style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textAlign: "center", padding: "4px 0" }}>{d}</div>
                  ))}
                  {cells.map((day, idx) => {
                    if (day === null) return <div key={`empty-${idx}`} />;
                    const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                    const dayAppts = byDay[iso] || [];
                    const isToday = iso === todayStr();
                    return (
                      <div key={iso} onClick={() => { setDate(iso); setViewMode("liste"); }}
                        style={{ minHeight: 78, border: "1px solid #E2E8F0", borderRadius: 8, padding: 6, cursor: "pointer", background: isToday ? "#E6F1FB" : "#fff" }}>
                        <div style={{ fontSize: 12, fontWeight: isToday ? 700 : 500, color: isToday ? "#185FA5" : "#334155" }}>{day}</div>
                        {dayAppts.length > 0 && (
                          <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: "#fff", background: "#185FA5", borderRadius: 10, padding: "1px 7px", display: "inline-block" }}>
                            {dayAppts.length} RDV
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {wizard && (
        <div onClick={resetWizard} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 520, padding: 24, marginTop: 40 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 16px" }}>
              {wizard.mode === "create" ? "Nouveau rendez-vous" : `Deplacer le RDV de ${wizard.appointment.patient_name || ""}`}
            </h3>
            {wizError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>{wizError}</div>}

            {wizard.mode === "create" && (
              <div style={{ marginBottom: 14 }}>
                <label style={lab}>Patient *</label>
                {patient ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: 8, background: "#F8FAFC" }}>
                    <span style={{ fontSize: 14 }}>{patient.first_name} {patient.last_name} <span style={{ color: "#94A3B8" }}>({patient.patient_number})</span></span>
                    <button onClick={() => setPatient(null)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Changer</button>
                  </div>
                ) : (
                  <>
                    <input style={iS} value={search} onChange={(e) => doSearch(e.target.value)} placeholder="Rechercher un patient (nom, numero, telephone...)" />
                    {results.length > 0 && (
                      <div style={{ marginTop: 6, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
                        {results.map((p) => (
                          <div key={p.patient_id} onClick={() => { setPatient(p); setResults([]); setSearch(""); }} style={{ padding: "9px 12px", borderTop: "1px solid #F1F5F9", cursor: "pointer", fontSize: 14 }}>
                            {p.first_name} {p.last_name} <span style={{ color: "#94A3B8", fontSize: 12 }}>({p.patient_number})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: wizard.mode === "create" ? "1fr 1fr" : "1fr", gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lab}>Date</label>
                <input type="date" style={iS} value={wizDate} onChange={(e) => setWizDate(e.target.value)} />
              </div>
              {wizard.mode === "create" && (
                <div>
                  <label style={lab}>Type d'acte</label>
                  <select style={iS} value={actTypeId} onChange={(e) => setActTypeId(e.target.value)}>
                    <option value="">— Non precise (15 min) —</option>
                    {actTypes.map((a) => <option key={a.act_type_id} value={a.act_type_id}>{a.name} ({a.duration_minutes} min)</option>)}
                  </select>
                </div>
              )}
            </div>

            {wizard.mode === "create" && (
              <div style={{ marginBottom: 14 }}>
                <label style={lab}>Notes (optionnel)</label>
                <input style={iS} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: Bilan de controle" />
              </div>
            )}

            <label style={lab}>Creneaux disponibles ({currentDuration()} min)</label>
            {loadingSlots ? (
              <div style={{ fontSize: 13, color: "#94A3B8", padding: "8px 0" }}>Recherche des creneaux...</div>
            ) : slots.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94A3B8", padding: "8px 0" }}>Aucun creneau disponible pour cette date.</div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 180, overflowY: "auto" }}>
                {slots.map((s) => (
                  <button
                    key={s}
                    disabled={saving || (wizard.mode === "create" && !patient)}
                    onClick={() => (wizard.mode === "create" ? confirmCreate(s) : confirmMove(s))}
                    style={{
                      padding: "7px 12px", borderRadius: 8, fontSize: 13, cursor: "pointer",
                      border: "1px solid #B5D4F4", background: "#E6F1FB", color: "#185FA5",
                      opacity: (wizard.mode === "create" && !patient) ? 0.5 : 1,
                    }}
                  >{s}</button>
                ))}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={resetWizard} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showWaitlist && (
        <div onClick={() => setShowWaitlist(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 560, padding: 24, marginTop: 40 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>Liste d'attente</h3>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 16 }}>Si un creneau se libere, les patients compatibles sont prevenus par email.</div>

            {can("agenda:manage") && (
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Ajouter un patient</div>
                {wlError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 13 }}>{wlError}</div>}
                {!wlPatient ? (
                  <div>
                    <input style={iS} placeholder="Rechercher un patient (nom, prenom)..." value={wlSearch} onChange={(e) => doWlSearch(e.target.value)} />
                    {wlResults.length > 0 && (
                      <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, marginTop: 6, maxHeight: 160, overflowY: "auto" }}>
                        {wlResults.map((p) => (
                          <div key={p.patient_id} onClick={() => { setWlPatient(p); setWlResults([]); setWlSearch(""); }} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #F1F5F9" }}>
                            {p.first_name} {p.last_name} {p.patient_number && <span style={{ color: "#94A3B8" }}>({p.patient_number})</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, marginBottom: 10 }}>
                      <span><strong>{wlPatient.first_name} {wlPatient.last_name}</strong></span>
                      <button onClick={() => setWlPatient(null)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Changer</button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                      <select style={iS} value={wlActTypeId} onChange={(e) => setWlActTypeId(e.target.value)}>
                        <option value="">Tout acte</option>
                        {actTypes.map((a) => <option key={a.act_type_id} value={a.act_type_id}>{a.name}</option>)}
                      </select>
                      <input type="date" style={iS} value={wlDate} onChange={(e) => setWlDate(e.target.value)} placeholder="Toute date" />
                    </div>
                    <input style={iS} placeholder="Notes (optionnel)" value={wlNotes} onChange={(e) => setWlNotes(e.target.value)} />
                    <button onClick={addToWaitlist} disabled={wlSaving} style={{ marginTop: 10, padding: "9px 16px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                      {wlSaving ? "Ajout..." : "Ajouter a la liste d'attente"}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Patients en attente ({waitlist.filter((w) => w.status === "attente" || w.status === "notifie").length})</div>
            {waitlist.filter((w) => w.status === "attente" || w.status === "notifie").length === 0 && (
              <div style={{ fontSize: 13, color: "#94A3B8", padding: "12px 0" }}>Aucun patient en liste d'attente.</div>
            )}
            {waitlist.filter((w) => w.status === "attente" || w.status === "notifie").map((w) => (
              <div key={w.waitlist_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F1F5F9" }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#0F172A" }}>{w.patient_name}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>
                    {w.act_type_name || "Tout acte"} · {w.desired_date || "Toute date"}
                    {w.status === "notifie" && <span style={{ marginLeft: 8, color: "#B45309", fontWeight: 600 }}>Notifie</span>}
                  </div>
                </div>
                {can("agenda:manage") && (
                  <span style={{ display: "flex", gap: 10 }}>
                    <button onClick={() => setWaitlistStatus(w, "confirme")} style={{ fontSize: 12, color: "#166534", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Confirme</button>
                    <button onClick={() => setWaitlistStatus(w, "annule")} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Retirer</button>
                  </span>
                )}
              </div>
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={() => setShowWaitlist(false)} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showSchedule && (
        <div onClick={() => setShowSchedule(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", paddingBottom: 40 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, width: "94%", maxWidth: 680, padding: 24, marginTop: 40 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0F172A", margin: "0 0 4px" }}>Horaires, capacite et fermetures</h3>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 16 }}>Definit les creneaux proposes dans l'agenda (section 25 du cahier des charges).</div>
            {scheduleError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 14, fontSize: 13 }}>{scheduleError}</div>}

            {scheduleLoading || !weekly ? (
              <div style={{ padding: 20, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>Chargement...</div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 10 }}>Horaires hebdomadaires</div>
                {SCHEDULE_WEEKDAY_LABELS.map((label, idx) => {
                  const day = String(idx);
                  const ranges = weekly[day] || [];
                  const isOpen = ranges.length > 0;
                  return (
                    <div key={day} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                      <label style={{ width: 110, fontSize: 13, display: "flex", alignItems: "center", gap: 6, paddingTop: 6 }}>
                        <input type="checkbox" checked={isOpen} onChange={(e) => toggleDayOpen(day, e.target.checked)} />
                        {label}
                      </label>
                      <div style={{ flex: 1 }}>
                        {isOpen ? (
                          <>
                            {ranges.map((r, i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                                <input type="time" value={r.start} onChange={(e) => setRangeField(day, i, "start", e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                                <span style={{ color: "#94A3B8", fontSize: 12 }}>a</span>
                                <input type="time" value={r.end} onChange={(e) => setRangeField(day, i, "end", e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                                {ranges.length > 1 && <button onClick={() => removeRange(day, i)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Retirer</button>}
                              </div>
                            ))}
                            <button onClick={() => addRange(day)} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0 }}>+ Ajouter une pause (ex: dejeuner)</button>
                          </>
                        ) : (
                          <span style={{ fontSize: 13, color: "#94A3B8" }}>Ferme</span>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
                  <label style={{ fontSize: 13, color: "#334155" }}>Capacite simultanee (rendez-vous en parallele) :</label>
                  <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value, 10) || 1))} style={{ width: 70, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                </div>

                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", margin: "20px 0 8px", paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>Confirmation obligatoire (section 23)</div>
                <label style={{ fontSize: 13, color: "#334155", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <input type="checkbox" checked={confirmRequired} onChange={(e) => setConfirmRequired(e.target.checked)} />
                  Exiger que le patient confirme son rendez-vous depuis le portail
                </label>
                {confirmRequired && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <label style={{ fontSize: 13, color: "#334155" }}>Delai avant le RDV pour confirmer (heures) :</label>
                    <input type="number" min={1} value={confirmDeadlineHours} onChange={(e) => setConfirmDeadlineHours(Math.max(1, parseInt(e.target.value, 10) || 24))} style={{ width: 70, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                  </div>
                )}
                <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>
                  Le systeme ne desiste jamais automatiquement : passe le delai sans confirmation, le personnel est seulement alerte et decide (relancer, annuler, remettre le creneau en liste d'attente). S'applique aux rendez-vous crees APRES l'enregistrement de ce reglage.
                </div>

                <button onClick={saveSchedule} disabled={scheduleSaving} style={{ marginTop: 14, padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {scheduleSaving ? "Enregistrement..." : "Enregistrer les horaires"}
                </button>

                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", margin: "24px 0 10px", paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>Fermetures exceptionnelles</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  <input type="date" value={newClosureDate} onChange={(e) => setNewClosureDate(e.target.value)} style={{ padding: "7px 10px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                  <label style={{ fontSize: 12, color: "#334155", display: "flex", alignItems: "center", gap: 4 }}>
                    <input type="checkbox" checked={newClosureFullDay} onChange={(e) => setNewClosureFullDay(e.target.checked)} /> Journee complete
                  </label>
                  {!newClosureFullDay && (
                    <>
                      <input type="time" value={newClosureStart} onChange={(e) => setNewClosureStart(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                      <span style={{ color: "#94A3B8", fontSize: 12 }}>a</span>
                      <input type="time" value={newClosureEnd} onChange={(e) => setNewClosureEnd(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13 }} />
                    </>
                  )}
                  <input placeholder="Motif (optionnel)" value={newClosureReason} onChange={(e) => setNewClosureReason(e.target.value)} style={{ padding: "7px 10px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13, flex: 1, minWidth: 140 }} />
                  <button onClick={addClosure} disabled={!newClosureDate} style={{ padding: "8px 14px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Ajouter</button>
                </div>
                {closures.length === 0 ? (
                  <div style={{ fontSize: 13, color: "#94A3B8", padding: "8px 0" }}>Aucune fermeture exceptionnelle a venir.</div>
                ) : closures.map((c) => (
                  <div key={c.closure_id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #F1F5F9" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "#0F172A" }}>{c.date}{c.start ? ` · ${c.start}-${c.end}` : " · journee complete"}</div>
                      {c.reason && <div style={{ fontSize: 12, color: "#94A3B8" }}>{c.reason}</div>}
                    </div>
                    <button onClick={() => removeClosure(c)} style={{ fontSize: 12, color: "#A32D2D", background: "none", border: "none", cursor: "pointer" }}>Retirer</button>
                  </div>
                ))}
              </>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
              <button onClick={() => setShowSchedule(false)} style={{ padding: "9px 16px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
